/**
 * Kaching Bundles — send the shades the customer actually picked.
 *
 * The app treats the theme form's [name="id"] as the single source of truth and
 * mirrors it onto every bundle slot, while the theme's ProductForm helper
 * rewrites that same input from its own variant picker on submit. The slot
 * choices lose that fight, so the cart used to get N× the main variant.
 * (Kaching's CartInterceptor would rewrite the request for us, but it only runs
 * when they enable the `intercept_cart_request` flag — off for this shop, so
 * `window.OpusNoATC` is never set.)
 *
 * This fixes the requests on their way out instead of repairing the cart after:
 *   1. Slot choices are recorded per slot as the customer makes them — a
 *      `change` for the native-select style, a listbox click for the swatch
 *      style. The app's own resets touch neither, so only real intent is kept.
 *   2. Both add calls are corrected against that one snapshot: the app's own
 *      units are re-pointed at the chosen shades, the theme's request takes
 *      the next free slot, and anything still uncovered is added in the same
 *      await — so the drawer refreshes once, already correct.
 *
 * Repairing afterwards (add, then trim the surplus) was tried first and is
 * wrong: for the split second the cart holds too many units, Kaching's gift
 * logic reads the higher tier, so trimming back left the customer with a gift
 * that is no longer free.
 *
 * Conservative by design:
 *   - only touches /cart/add.js requests that carry a `__kaching_bundles`
 *     property, i.e. adds that came from a bundle deal;
 *   - leaves the request alone when the choice already matches;
 *   - extra slots are added with `kaching_bundles=true`, which the app's own
 *     interceptor skips, so nothing double-fires if that flag is turned on;
 *   - any failure falls through to the original request.
 */
(function () {
  'use strict';

  // A slot is `.kaching-bundles-option`: a native <select> in the dropdown
  // style, a <div data-value> in the swatch-dropdown style.
  var SLOT = '.kaching-bundles-option';
  var OPTION = '.kaching-bundles-option-value';
  var BAR = '.kaching-bundles__bar';
  var KACHING_PROP = 'properties[__kaching_bundles]';

  var intent = new WeakMap();
  var pending = null;

  function slotValue(slot) {
    return slot.tagName === 'SELECT' ? slot.value : slot.getAttribute('data-value') || '';
  }

  function selectedBar() {
    var checked = document.querySelector(BAR + ' input[type="radio"]:checked');
    return checked ? checked.closest(BAR) : document.querySelector(BAR + '--selected');
  }

  /** Variant title -> id, from the PDP form's own variant select. */
  function titleToVariantId() {
    var map = {};
    var select = document.querySelector('form.pdp-form [name="id"]');
    if (select && select.options) {
      [].forEach.call(select.options, function (option) {
        map[option.textContent.trim()] = Number(option.value);
      });
    }
    return map;
  }

  /** One variant id per slot of the selected tier, or null if not resolvable. */
  function chosenVariantIds() {
    var bar = selectedBar();
    if (!bar) return null;

    var slots = [].slice.call(bar.querySelectorAll(SLOT));
    if (!slots.length) return null;

    var map = titleToVariantId();
    var ids = [];
    for (var i = 0; i < slots.length; i++) {
      // Remembered per slot, so the app resetting one slot can't overwrite
      // what the customer picked in another.
      var title = intent.has(slots[i]) ? intent.get(slots[i]) : slotValue(slots[i]);
      var id = map[String(title).trim()];
      if (!id) return null; // unknown label — leave the request alone
      ids.push(id);
    }
    return ids;
  }

  // Native-select style: only real customer changes fire `change` (the app's
  // own resets assign .value silently).
  document.addEventListener(
    'change',
    function (e) {
      var slot = e.target && e.target.closest && e.target.closest(SLOT);
      if (slot && slot.tagName === 'SELECT') intent.set(slot, slot.value);
    },
    true
  );

  // Swatch-dropdown style: the choice is a click on a listbox option.
  document.addEventListener(
    'click',
    function (e) {
      var option = e.target && e.target.closest && e.target.closest(OPTION);
      if (!option) return;
      var slot = option.closest(SLOT);
      var value = option.getAttribute('data-value');
      if (slot && value) intent.set(slot, value);
    },
    true
  );

  /* ---------------- request rewriting ---------------- */

  function isBundleAdd(url, body) {
    return (
      typeof body === 'string' &&
      /\/cart\/add(\.js)?/.test(url) &&
      url.indexOf('kaching_bundles=true') === -1 &&
      body.indexOf(encodeURIComponent(KACHING_PROP)) !== -1
    );
  }

  function isKachingPartial(url, body) {
    return (
      typeof body === 'string' &&
      /\/cart\/add(\.js)?/.test(url) &&
      url.indexOf('kaching_bundles=true') !== -1 &&
      url.indexOf('partial=true') !== -1
    );
  }

  function isGiftItem(item) {
    var raw = item.properties && item.properties.__kaching_bundles;
    return typeof raw === 'string' && raw.indexOf('"gift"') !== -1;
  }

  function isBxgyItem(item) {
    var raw = item.properties && item.properties.__kaching_bundles;
    return typeof raw === 'string' && raw.indexOf('"bxgy":true') !== -1;
  }

  /**
   * Point the app's own units at the remaining slots.
   *
   * How the app splits a bundle depends on the deal type: a quantity tier
   * ("Купи 2") puts every unit on the theme's form and this call carries only
   * gifts, while a BxGy tier ("Купи 2, вземи 1 безплатно") sends the paid
   * extras AND the free unit here. The free one (`"bxgy":true`) is the one
   * that ignores its slot and repeats the main variant.
   *
   * Gifts are left untouched. Paid units take slots from the front, the free
   * unit takes the last one — which is where the tier shows it on screen.
   * Units left without a slot are dropped, since the theme's request and our
   * extras cover the rest and leaving them would overfill the cart.
   *
   * Whether the theme's form gets to add anything at all depends on the app:
   * when the customer mixes shades it sometimes adds every unit here and never
   * submits the form. That's why slots are consumed from one shared queue
   * instead of assuming the form covers the first one.
   */
  function retargetPartial(body, remaining) {
    var payload = JSON.parse(body);
    var items = (payload && payload.items) || [];
    if (!items.length) return null;

    var mains = items.filter(function (item) {
      return !isGiftItem(item);
    });
    if (!mains.length) return null; // gifts only — nothing to re-point
    if (
      mains.some(function (item) {
        return item.quantity !== 1;
      })
    ) {
      return null; // multi-unit lines can't be split slot by slot
    }

    var queue = remaining.slice();
    var keep = [];

    mains
      .filter(function (item) {
        return !isBxgyItem(item);
      })
      .forEach(function (item) {
        if (!queue.length) return; // no slot left for this unit — drop it
        item.id = queue.shift();
        keep.push(item);
      });

    mains.filter(isBxgyItem).forEach(function (item) {
      if (!queue.length) return;
      item.id = queue.pop(); // the free unit is the tier's last slot
      keep.push(item);
    });

    payload.items = items.filter(function (item) {
      return isGiftItem(item) || keep.indexOf(item) !== -1;
    });
    return { body: JSON.stringify(payload), remaining: queue };
  }

  /**
   * Corrects the theme's own request: it covers the first slot. `pending`
   * carries the rest, which the app's call re-points to (BxGy tiers) or we add
   * ourselves (quantity tiers, where the app only sends gifts).
   */
  function plan(body) {
    var state = pending;
    // Every slot already covered by the app's own call — leave this alone.
    if (!state || !state.remaining.length) return null;

    var params = new URLSearchParams(body);
    var currentId = Number(params.get('id'));
    if (!currentId) return null;

    var next = state.remaining[0];
    var formQty = Number(params.get('quantity') || 1);
    var uniform = state.ids.every(function (id) {
      return id === state.ids[0];
    });
    // Nothing to correct: the form carries the chosen variant, every slot
    // repeats it and its quantity already covers them.
    if (next === currentId && uniform && formQty >= state.remaining.length) {
      state.remaining = [];
      return null;
    }

    state.remaining.shift();
    params.set('id', String(next));
    // The form carries the whole tier quantity on quantity tiers; the slots
    // left over are covered separately, so this request only covers one.
    if (state.remaining.length && formQty > 1) params.set('quantity', '1');

    return { body: params.toString(), properties: params.get(KACHING_PROP), state: state };
  }

  function addExtras(extras, kachingProperty) {
    var items = extras.map(function (id) {
      return {
        id: id,
        quantity: 1,
        properties: kachingProperty ? { __kaching_bundles: kachingProperty } : undefined,
      };
    });
    return fetch('/cart/add.js?kaching_bundles=true', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items }),
    }).catch(function (e) {
      console.error('[kaching-bundle-variants] extra slots failed', e);
    });
  }

  // The add-to-cart click is where the customer's choice is frozen: the app's
  // own call fires first, the theme's second, and both are corrected against
  // this one snapshot.
  //
  // The app re-clicks the button programmatically as part of its own flow. A
  // fresh snapshot there would forget what its call already covered and the
  // extra slots would be added twice, so an untrusted click only starts a new
  // snapshot when there is no live one.
  var SNAPSHOT_TTL_MS = 3000;

  document.addEventListener(
    'click',
    function (e) {
      var button = e.target.closest('button[name="add"], button.product-form__cart-submit');
      if (!button || button.disabled) return;
      if (!e.isTrusted && pending && Date.now() - pending.at < SNAPSHOT_TTL_MS) return;
      var ids = chosenVariantIds();
      pending = ids ? { ids: ids, remaining: ids.slice(), at: Date.now() } : null;
    },
    true
  );

  function withBody(init, body) {
    var patched = {};
    for (var key in init) patched[key] = init[key];
    patched.body = body;
    return patched;
  }

  var originalFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var body = init && init.body;
    var step = null;

    try {
      if (isKachingPartial(url, body) && pending && pending.remaining.length) {
        var retarget = retargetPartial(body, pending.remaining);
        if (retarget) {
          pending.remaining = retarget.remaining;
          return originalFetch.call(this, input, withBody(init, retarget.body));
        }
        return originalFetch.apply(this, arguments);
      }
      if (isBundleAdd(url, body)) step = plan(body);
    } catch (e) {
      console.error('[kaching-bundle-variants]', e);
    }
    if (!step) return originalFetch.apply(this, arguments);

    // Add whatever no request covered before handing the response back, so the
    // theme refreshes the drawer once, with the full bundle already in place.
    return originalFetch.call(this, input, withBody(init, step.body)).then(function (res) {
      var leftovers = step.state.remaining;
      step.state.remaining = [];
      if (!leftovers.length || !res.ok) return res;
      return addExtras(leftovers, step.properties).then(function () {
        return res;
      });
    });
  };
})();
